import {
    Box,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Icon,
    IconButton,
    Typography,
} from "@mui/material";
import CustomDialog from "components/custom/CustomDialog";
import { CustomButton } from "components/roundedbutton/CustomButton";
import React, { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { FichaImagemAssociacao } from "redux/features/imagemReducer";
import { theme } from "theme";
import { isEmpty } from "utils/util";

export type TCustomProdutoImagensDialogProps = {
    uuidProduto: string;
    onClose: () => void;
    imagens: FichaImagemAssociacao | null;
};

const initialState = {
    currentIndex: 0,
    imageLoading: true as boolean,
    thumbsLoading: [] as boolean[],
    zoomed: false as boolean,
    zoomOrigin: { x: 50, y: 50 } as { x: number; y: number },
};

const CustomProdutoImagensDialog = (props: TCustomProdutoImagensDialogProps) => {
    const [stateLocal, setStateLocal] = useState(initialState);

    const thumbRefs = useRef<(HTMLDivElement | null)[]>([]);
    const mainBoxRef = useRef<HTMLDivElement | null>(null);

    const imagens = useMemo(() => props.imagens?.imagens ?? [], [props.imagens?.imagens]);
    const hasImagens = !isEmpty(imagens);
    const currentImage = hasImagens ? imagens[stateLocal.currentIndex] : undefined;

    useEffect(() => {
        const el = thumbRefs.current[stateLocal.currentIndex];
        el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [stateLocal.currentIndex]);

    useEffect(() => {
        const len = imagens.length;
        if (len) {
            setStateLocal(s => ({ ...s, thumbsLoading: Array(len).fill(true) }));
        }
    }, [imagens.length]);

    useEffect(() => {
        if (!currentImage?.url) {
            setStateLocal(s => ({ ...s, imageLoading: false }));
            return;
        }
        setStateLocal(s => ({ ...s, imageLoading: true }));
    }, [currentImage?.url]);

    const handlePrev = () => {
        setStateLocal(prev => {
            const total = imagens.length;
            if (!total) return prev;
            const nextIndex = (prev.currentIndex - 1 + total) % total;
            return { ...prev, currentIndex: nextIndex, imageLoading: true, zoomed: false, zoomOrigin: { x: 50, y: 50 } };
        });
    };

    const handleNext = () => {
        setStateLocal(prev => {
            const total = imagens.length;
            if (!total) return prev;
            const nextIndex = (prev.currentIndex + 1) % total;
            return { ...prev, currentIndex: nextIndex, imageLoading: true, zoomed: false, zoomOrigin: { x: 50, y: 50 } };
        });
    };

    const handleToggleZoom = (event: MouseEvent<HTMLDivElement>) => {
        if (!hasImagens || stateLocal.imageLoading) return;
        const container = mainBoxRef.current;
        if (!container) {
            setStateLocal(prev => ({ ...prev, zoomed: !prev.zoomed }));
            return;
        }
        const rect = container.getBoundingClientRect();
        const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
        const yPercent = ((event.clientY - rect.top) / rect.height) * 100;
        setStateLocal(prev => ({ ...prev, zoomed: !prev.zoomed, zoomOrigin: { x: xPercent, y: yPercent } }));
    };

    const handleThumbClick = (index: number) => {
        setStateLocal(prev => ({
            ...prev,
            currentIndex: index,
            imageLoading: index !== prev.currentIndex,
            zoomed: false,
            zoomOrigin: { x: 50, y: 50 },
        }));
    };

    return (
        <CustomDialog open onClose={props.onClose} fullWidth maxWidth={"md"}>
            <Grid xs={12} sm={12} lg={12} style={{ minHeight: "150px" }}>
                <DialogTitle id="dialog-title" textAlign={"center"}>
                    <Typography fontSize={24} paddingTop={0}>
                        Imagens do produto
                    </Typography>
                </DialogTitle>

                <DialogContent
                    id="dialog-content"
                    sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}
                >
                    {hasImagens ? (
                        <>
                            <Grid container alignItems="center" justifyContent="center" sx={{ mb: 2, position: "relative" }}>
                                {imagens.length > 1 && (
                                    <IconButton
                                        onClick={handlePrev}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: 10,
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        <Icon className="fa-solid fa-chevron-left" />
                                    </IconButton>
                                )}

                                <Box
                                    ref={mainBoxRef}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 900,
                                        height: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        border: "1px solid #ddd",
                                        mx: 1,
                                        position: "relative",
                                        bgcolor: "#fafafa",
                                        cursor: hasImagens ? (stateLocal.zoomed ? "zoom-out" : "zoom-in") : "default",
                                    }}
                                    onClick={handleToggleZoom}
                                >
                                    {stateLocal.imageLoading && (
                                        <CircularProgress size={32} sx={{ position: "absolute" }} />
                                    )}

                                    {currentImage && (
                                        <img
                                            src={currentImage.url}
                                            alt={currentImage.infoAdicional ?? "Imagem do produto"}
                                            onLoad={() => setStateLocal(prev => ({ ...prev, imageLoading: false }))}
                                            onError={() => setStateLocal(prev => ({ ...prev, imageLoading: false }))}
                                            loading="lazy"
                                            decoding="async"
                                            draggable={false}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                                display: "block",
                                                visibility: stateLocal.imageLoading ? "hidden" : "visible",
                                                transform: stateLocal.zoomed ? "scale(2)" : "scale(1)",
                                                transformOrigin: `${stateLocal.zoomOrigin.x}% ${stateLocal.zoomOrigin.y}%`,
                                                transition: "transform 0.25s ease-out",
                                            }}
                                        />
                                    )}
                                </Box>

                                {imagens.length > 1 && (
                                    <IconButton
                                        onClick={handleNext}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            right: 10,
                                            transform: "translateY(-50%)",
                                            zIndex: 1,
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        <Icon className="fa-solid fa-chevron-right" />
                                    </IconButton>
                                )}
                            </Grid>

                            <Typography variant="body2">
                                {stateLocal.currentIndex + 1} / {imagens.length}
                            </Typography>

                            {imagens.length > 1 && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        width: "100%",
                                        maxWidth: 500,
                                        overflowX: "auto",
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 1,
                                        px: 1,
                                        justifyContent: imagens.length > 5 ? "flex-start" : "center",
                                    }}
                                >
                                    {imagens.map((img, index) => {
                                        const isThumbLoading = stateLocal.thumbsLoading[index];
                                        return (
                                            <Box
                                                key={img.url ?? index}
                                                ref={el => {
                                                    thumbRefs.current[index] = el as HTMLDivElement | null;
                                                }}
                                                onClick={() => handleThumbClick(index)}
                                                sx={{
                                                    minWidth: 60,
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    border: index === stateLocal.currentIndex ? "2px solid #1976d2" : "1px solid #ddd",
                                                    cursor: "pointer",
                                                    position: "relative",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {isThumbLoading && (
                                                    <CircularProgress
                                                        size={18}
                                                        sx={{
                                                            position: "absolute",
                                                            top: "40%",
                                                            left: "40%",
                                                            transform: "translate(-50%, -50%)",
                                                            zIndex: 1,
                                                        }}
                                                    />
                                                )}

                                                <img
                                                    src={img.url}
                                                    alt={img.infoAdicional ?? "Thumb"}
                                                    onLoad={() =>
                                                        setStateLocal(prev => {
                                                            const copy = prev.thumbsLoading.slice();
                                                            copy[index] = false;
                                                            return { ...prev, thumbsLoading: copy };
                                                        })
                                                    }
                                                    loading="lazy"
                                                    decoding="async"
                                                    draggable={false}
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block",
                                                        opacity: isThumbLoading ? 0 : 1,
                                                        transition: "opacity 0.2s ease-in-out",
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography>
                            Nenhuma imagem encontrada para este produto.
                        </Typography>
                    )}
                </DialogContent>
            </Grid>

            <DialogActions>
                <CustomButton
                    onClick={props.onClose}
                    variant="contained"
                    color="error"
                    startIcon={<Icon className="fa-solid fa-xmark" />}
                >
                    Fechar
                </CustomButton>
            </DialogActions>
        </CustomDialog>
    );
};

export default CustomProdutoImagensDialog;
